import { memo, useCallback, useState } from 'react';
import classnames from 'classnames';

import { WithError } from '@eva/types/dto';

import type { ControlDescriptor, ControlState, GroupedTableClassNames, RowState } from '../types';

import Control from '../Control';

import './Controls.scss';

export interface ControlsProps<E, G> {
  entity: E;
  groups: G[];
  rowState: RowState;
  controls: ControlDescriptor<E, G>[];
  classNames: GroupedTableClassNames;
  getEntityId: (entity: E) => number;
  updateRowState: (changes: Partial<RowState>) => void;
}

const Controls = <E, G extends WithError>({
  entity,
  groups,
  controls,
  rowState,
  classNames,
  getEntityId,
  updateRowState,
}: ControlsProps<E, G>) => {
  const [controlStates, setControlStates] = useState<{
    [key: string]: ControlState;
  }>({});

  const updateControlState = useCallback((controlId, changes: Partial<ControlState>) => {
    setControlStates((currentState) => ({
      ...currentState,
      [controlId]: {
        ...(currentState[controlId] || {}),
        ...changes,
      },
    }));
  }, []);

  const controlsClassName = classnames({
    [classNames.controlsClassName]: true,
  });

  const controlClassName = classnames({
    [classNames.controlClassName]: true,
  });

  return (
    <div className={controlsClassName}>
      {controls?.map((control) => {
        const state = controlStates[control.actionId];
        const key = `${getEntityId(entity)}-${control.actionId}`;

        const show =
          typeof control.showCondition === 'function' ? control.showCondition(entity, state, rowState) : true;

        return (
          <span key={key} className={controlClassName}>
            {show && (
              <Control
                state={state}
                entity={entity}
                groups={groups}
                control={control}
                rowState={rowState}
                getEntityId={getEntityId}
                updateState={updateControlState}
                updateRowState={updateRowState}
              />
            )}
          </span>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(Controls);
